export const PUBLIC_LOCALES = {
  en: 'English',
  'zh-hans': '简体中文',
  de: 'Deutsch',
  es: 'Español',
  it: 'Italiano',
  cs: 'Čeština',
  sv: 'Svenska',
  id: 'Bahasa Indonesia',
  nl: 'Nederlands',
  vi: 'Tiếng Việt',
} as const;
export type PublicLocale = keyof typeof PUBLIC_LOCALES;
export const PUBLIC_LANGUAGE_KEY = 'revelationary.publicLanguage';
export const bibleLocale: Record<string, PublicLocale> = {
  kjv: 'en',
  fbv: 'en',
  cuv: 'zh-hans',
  lut: 'de',
  'es-rv09': 'es',
  'it-db1885': 'it',
  'cs-bkr': 'cs',
  'sv-skb': 'sv',
  'id-tsi': 'id',
  'nl-nld1939': 'nl',
  'vi-ovcb': 'vi',
};
export function supportedLocale(value?: string | null): PublicLocale | undefined {
  const tag = value?.toLowerCase().replaceAll('_', '-');
  if (!tag) {
    return undefined;
  }
  if (tag === 'zh' || /^zh-(hans|cn|sg)(-|$)/.test(tag)) {
    return 'zh-hans';
  }
  const base = tag.split('-')[0];
  return base !== 'zh' && Object.prototype.hasOwnProperty.call(PUBLIC_LOCALES, base)
    ? (base as PublicLocale)
    : undefined;
}
export function publicRoute(pathname: string): { locale?: PublicLocale; path: string } {
  const match = pathname.match(/^\/(en|zh-hans|de|es|it|cs|sv|id|nl|vi)(\/.*|$)/);
  return match ? { locale: match[1] as PublicLocale, path: match[2] || '/' } : { path: pathname };
}
export function resolvePublicLocale(options: {
  pathname: string;
  saved?: string | null;
  bible?: string;
  browsers?: readonly string[];
}): PublicLocale {
  return (
    publicRoute(options.pathname).locale ||
    supportedLocale(options.saved) ||
    bibleLocale[options.bible || ''] ||
    options.browsers?.map(supportedLocale).find(Boolean) ||
    'en'
  );
}
export function localizedPath(path: string, locale: PublicLocale): string {
  if (!path.startsWith('/') || path.startsWith('//')) {
    return path;
  }
  const clean = publicRoute(path).path;
  // Reader routes, callbacks and shared assets stay at their existing paths.
  if (
    /^\/study(?:\/|#|\?|$)/.test(clean) ||
    /^\/#\//.test(clean) ||
    /^\/(static|assets)\//.test(clean) ||
    /\.[a-z0-9]+(?:[?#]|$)/i.test(clean)
  ) {
    return clean;
  }
  return locale === 'en' ? clean : `/${locale}${clean}`;
}
export const languageTag = (locale: PublicLocale) => (locale === 'zh-hans' ? 'zh-Hans' : locale);

/** Carry only a supported locale through the existing fixed sign-in return. */
export function localizedLoginPath(path: string, locale: PublicLocale): string {
  if (!path.startsWith('/study/#/login') || locale === 'en') {
    return path;
  }
  const [route, query = ''] = path.split('?');
  const params = new URLSearchParams(query);
  params.set('lang', locale);
  return `${route}?${params}`;
}
